import { Navigate, Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { lazyPage } from "@/lib/lazy-page";

const NewProjectPage = lazyPage(() => import("@/pages/create/NewProjectPage"));
const NewProjectGroupPage = lazyPage(
  () => import("@/pages/create/NewProjectGroupPage"),
);
const NewCompanyPage = lazyPage(() => import("@/pages/create/NewCompanyPage"));
const NewPersonPage = lazyPage(() => import("@/pages/create/NewPersonPage"));
const NewEquipmentPage = lazyPage(
  () => import("@/pages/create/NewEquipmentPage"),
);
const NewVehiclePage = lazyPage(() => import("@/pages/create/NewVehiclePage"));
const NewStudioPage = lazyPage(() => import("@/pages/create/NewStudioPage"));
const ProjectsPage = lazyPage(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage = lazyPage(() => import("@/pages/ProjectDetailPage"));
const ProjectCalendarPage = lazyPage(
  () => import("@/pages/ProjectCalendarPage"),
);
const ProjectGroupsPage = lazyPage(() => import("@/pages/ProjectGroupsPage"));
const ProjectGroupDetailPage = lazyPage(
  () => import("@/pages/ProjectGroupDetailPage"),
);
const CompaniesPage = lazyPage(() => import("@/pages/CompaniesPage"));
const CompanyDetailPage = lazyPage(() => import("@/pages/CompanyDetailPage"));
const PeoplePage = lazyPage(() => import("@/pages/PeoplePage"));
const PersonDetailPage = lazyPage(() => import("@/pages/PersonDetailPage"));
const CrewPlanningPage = lazyPage(() => import("@/pages/CrewPlanningPage"));
const EquipmentPage = lazyPage(() => import("@/pages/EquipmentPage"));
const EquipmentDetailPage = lazyPage(
  () => import("@/pages/EquipmentDetailPage"),
);
const EquipmentTagPage = lazyPage(() => import("@/pages/EquipmentTagPage"));
const EquipmentUntaggedPage = lazyPage(
  () => import("@/pages/EquipmentUntaggedPage"),
);
const EquipmentTagsPage = lazyPage(() => import("@/pages/EquipmentTagsPage"));
const VehiclesPage = lazyPage(() => import("@/pages/VehiclesPage"));
const VehicleDetailPage = lazyPage(() => import("@/pages/VehicleDetailPage"));
const VehiclePlanningPage = lazyPage(
  () => import("@/pages/VehiclePlanningPage"),
);
const StudiosPage = lazyPage(() => import("@/pages/StudiosPage"));
const StudioDetailPage = lazyPage(() => import("@/pages/StudioDetailPage"));
const ConfigEditorPage = lazyPage(() => import("@/pages/ConfigEditorPage"));
const PatchSheetPage = lazyPage(() => import("@/pages/PatchSheetPage"));
const MeetingNotesPage = lazyPage(() => import("@/pages/MeetingNotesPage"));
const MeetingNoteDetailPage = lazyPage(
  () => import("@/pages/MeetingNoteDetailPage"),
);
const TasksPage = lazyPage(() => import("@/pages/TasksPage"));
const TaskPanelPage = lazyPage(() => import("@/pages/TaskPanelPage"));
const SignalTracePage = lazyPage(() => import("@/pages/SignalTracePage"));
const NetworkCoveragePage = lazyPage(
  () => import("@/pages/NetworkCoveragePage"),
);
const SimCardsPage = lazyPage(() => import("@/pages/SimCardsPage"));
const ToolsPage = lazyPage(() => import("@/pages/ToolsPage"));
const FileArchivePage = lazyPage(() => import("@/pages/FileArchivePage"));
const AiAgentPage = lazyPage(() => import("@/pages/AiAgentPage"));
const CloudMediaPage = lazyPage(() => import("@/pages/CloudMediaPage"));
const CloudComputersPage = lazyPage(() => import("@/pages/CloudComputersPage"));
const HttpsForwardsPage = lazyPage(() => import("@/pages/HttpsForwardsPage"));
const ManagedServersPage = lazyPage(() => import("@/pages/ManagedServersPage"));
const DefaultFunctionsPage = lazyPage(
  () => import("@/pages/DefaultFunctionsPage"),
);
const AdminMaintenancePage = lazyPage(
  () => import("@/pages/AdminMaintenancePage"),
);
const AdminMailCatcherPage = lazyPage(
  () => import("@/pages/AdminMailCatcherPage"),
);
const SettingsPage = lazyPage(() => import("@/pages/SettingsPage"));
const HRPage = lazyPage(() => import("@/pages/HRPage"));
const MyBalancePage = lazyPage(() => import("@/pages/MyBalancePage"));
const QuoteTemplatesPage = lazyPage(() => import("@/pages/QuoteTemplatesPage"));
const DashboardPage = lazyPage(() => import("@/pages/DashboardPage"));
const AdminSettingsPage = lazyPage(() => import("@/pages/AdminSettingsPage"));
const GlobalDocsPage = lazyPage(() => import("@/pages/GlobalDocsPage"));
const FinancePage = lazyPage(() => import("@/pages/FinancePage"));

/**
 * Routes of the main layout. Shared by the main view and the side panel, so
 * every page can also open in the panel (R1).
 */
export const mainRoutes = (
  <>
    <Route index element={<DashboardPage />} />
    <Route path="my-balance" element={<MyBalancePage />} />
    <Route path="projects" element={<ProjectsPage />} />
    <Route
      path="projects/new"
      element={
        <ProtectedRoute>
          <NewProjectPage />
        </ProtectedRoute>
      }
    />
    <Route path="projects/:id/*" element={<ProjectDetailPage />} />
    <Route path="calendar" element={<ProjectCalendarPage />} />
    <Route
      path="projects/calendar"
      element={<Navigate to="/calendar" replace />}
    />
    <Route
      path="projects/groups"
      element={
        <ProtectedRoute requireEmployee>
          <ProjectGroupsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="projects/groups/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewProjectGroupPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="projects/groups/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <ProjectGroupDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="companies"
      element={
        <ProtectedRoute requireEmployee>
          <CompaniesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="companies/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewCompanyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="companies/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <CompanyDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people"
      element={
        <ProtectedRoute requireEmployee>
          <PeoplePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewPersonPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <PersonDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/employees"
      element={
        <ProtectedRoute requireEmployee>
          <PeoplePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/employees/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewPersonPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/freelancers"
      element={
        <ProtectedRoute requireEmployee>
          <PeoplePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="people/freelancers/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewPersonPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="crew-planning"
      element={
        <ProtectedRoute requireEmployee>
          <CrewPlanningPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment"
      element={
        <ProtectedRoute requireEmployee>
          <EquipmentPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="vehicles"
      element={
        <ProtectedRoute requireEmployee>
          <VehiclesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="vehicles/planning"
      element={
        <ProtectedRoute requireEmployee>
          <VehiclePlanningPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="vehicles/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewVehiclePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="vehicles/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <VehicleDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment/tags"
      element={
        <ProtectedRoute requireEmployee>
          <EquipmentTagsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment/untagged"
      element={
        <ProtectedRoute requireEmployee>
          <EquipmentUntaggedPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment/tag/:tagId"
      element={
        <ProtectedRoute requireEmployee>
          <EquipmentTagPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewEquipmentPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="equipment/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <EquipmentDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="studios"
      element={
        <ProtectedRoute requireEmployee>
          <StudiosPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="studios/new"
      element={
        <ProtectedRoute requireEmployee>
          <NewStudioPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="studios/:id/*"
      element={
        <ProtectedRoute requireEmployee>
          <StudioDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="studios/:studioId/config/:configId/*"
      element={
        <ProtectedRoute requireEmployee>
          <ConfigEditorPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="studios/:studioId/config/:configId/patch-sheet"
      element={
        <ProtectedRoute requireEmployee>
          <PatchSheetPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="signal-trace/:studioId/:portId"
      element={
        <ProtectedRoute requireEmployee>
          <SignalTracePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="tools"
      element={
        <ProtectedRoute requireEmployee>
          <ToolsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="cloud-media"
      element={
        <ProtectedRoute requireEmployee>
          <CloudMediaPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="cloud-computers"
      element={
        <ProtectedRoute requireEmployee>
          <CloudComputersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="file-archive"
      element={
        <ProtectedRoute requireEmployee>
          <FileArchivePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="https-forwards"
      element={
        <ProtectedRoute requireEmployee>
          <HttpsForwardsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="managed-servers"
      element={
        <ProtectedRoute requireEmployee>
          <ManagedServersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="docs/*"
      element={
        <ProtectedRoute requireEmployee>
          <GlobalDocsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="finance"
      element={
        <ProtectedRoute requireEmployee>
          <FinancePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="finance/invoices/:invoiceId"
      element={
        <ProtectedRoute requireEmployee>
          <FinancePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="meeting-notes"
      element={
        <ProtectedRoute requireEmployee>
          <MeetingNotesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="meeting-notes/management"
      element={
        <ProtectedRoute requireEmployee>
          <MeetingNotesPage managementOnly />
        </ProtectedRoute>
      }
    />
    <Route
      path="meeting-notes/:id"
      element={
        <ProtectedRoute requireEmployee>
          <MeetingNoteDetailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="tasks"
      element={
        <ProtectedRoute requireEmployee>
          <TasksPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="tasks/new"
      element={
        <ProtectedRoute requireEmployee>
          <TaskPanelPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="tasks/:taskId"
      element={
        <ProtectedRoute requireEmployee>
          <TaskPanelPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="network-coverage"
      element={
        <ProtectedRoute requireEmployee>
          <NetworkCoveragePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="sims"
      element={
        <ProtectedRoute requireEmployee>
          <SimCardsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="ai-agent"
      element={
        <ProtectedRoute requireEmployee>
          <AiAgentPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="admin/maintenance"
      element={
        <ProtectedRoute requireAdmin>
          <AdminMaintenancePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="admin/mail-catcher"
      element={
        <ProtectedRoute requireAdmin>
          <AdminMailCatcherPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="admin/settings"
      element={
        <ProtectedRoute requireAdmin>
          <AdminSettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="admin/default-functions"
      element={
        <ProtectedRoute requireAdmin>
          <DefaultFunctionsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="hr"
      element={
        <ProtectedRoute requireAdmin>
          <HRPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="settings"
      element={
        <ProtectedRoute requireEmployee>
          <SettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="admin/quote-templates"
      element={
        <ProtectedRoute requireAdmin>
          <QuoteTemplatesPage />
        </ProtectedRoute>
      }
    />
  </>
);
